import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  Camera, 
  Calendar,
  Clock,
  Check,
  Loader2,
  ChevronLeft,
  ChevronRight,
  User,
  Mail,
  Phone,
  MessageSquare
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS_SHORT = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];
const MONTHS = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];

export default function PublicBookingPage() {
  const { subdomain } = useParams();
  const [loading, setLoading] = useState(true);
  const [photographer, setPhotographer] = useState(null);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [step, setStep] = useState(1); // 1: service, 2: date/time, 3: details, 4: confirmation
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    notes: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);

  useEffect(() => {
    fetchData();
  }, [subdomain]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/public/booking/${subdomain}`);
      setPhotographer(response.data.photographer);
      setServices(response.data.services);
      setAvailability(response.data.availability);
      setBookedSlots(response.data.booked_slots);
    } catch (error) {
      console.error('Failed to fetch booking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    const startDay = (firstDay.getDay() + 6) % 7;
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateAvailable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return false;
    
    const dayOfWeek = (date.getDay() + 6) % 7;
    const dayAvail = availability.find(a => a.day_of_week === dayOfWeek);
    return dayAvail?.is_available || false;
  };

  const getAvailableTimes = () => {
    if (!selectedDate || !selectedService) return [];
    
    const dayOfWeek = (selectedDate.getDay() + 6) % 7;
    const dayAvail = availability.find(a => a.day_of_week === dayOfWeek);
    if (!dayAvail || !dayAvail.is_available) return [];
    
    const dateStr = selectedDate.toISOString().split('T')[0];
    const bookedForDay = bookedSlots.filter(b => b.date === dateStr);
    
    const times = [];
    const [startHour, startMin] = dayAvail.start_time.split(':').map(Number);
    const [endHour, endMin] = dayAvail.end_time.split(':').map(Number);
    
    let currentHour = startHour;
    let currentMin = startMin;
    
    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const timeStr = `${currentHour.toString().padStart(2, '0')}:${currentMin.toString().padStart(2, '0')}`;
      
      // Check if this slot is booked
      const isBooked = bookedForDay.some(b => b.time === timeStr);
      if (!isBooked) {
        times.push(timeStr);
      }
      
      // Increment by 30 minutes
      currentMin += 30;
      if (currentMin >= 60) {
        currentMin = 0;
        currentHour++;
      }
    }
    
    return times;
  };

  const handleSubmit = async () => {
    if (!selectedService || !selectedDate || !selectedTime || !formData.client_name || !formData.client_email) {
      toast.error('Wypełnij wszystkie wymagane pola');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/booking/${subdomain}`, {
        service_id: selectedService.id,
        date: selectedDate.toISOString().split('T')[0],
        time: selectedTime,
        ...formData
      });
      setBookingComplete(true);
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd rezerwacji');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!photographer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Nie znaleziono fotografa</h2>
            <p className="text-muted-foreground">Sprawdź poprawność linku</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const brandingColor = photographer.branding?.primary_color || '#FAFAFA';

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <Camera className="w-8 h-8" style={{ color: brandingColor }} />
            <div>
              <h1 className="text-2xl font-bold">{photographer.name}</h1>
              <p className="text-muted-foreground">Rezerwacja sesji</p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
              }`}>
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 4 && <div className={`w-12 h-1 ${step > s ? 'bg-primary' : 'bg-secondary'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Select Service */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <h2 className="text-xl font-semibold mb-6 text-center">Wybierz usługę</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map(service => (
                <Card 
                  key={service.id}
                  className={`cursor-pointer transition-all hover:border-primary ${
                    selectedService?.id === service.id ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setSelectedService(service)}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: service.color }} />
                      <h3 className="font-semibold">{service.name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{service.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        {service.duration_minutes} min
                      </div>
                      <span className="text-lg font-bold">{service.price} zł</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="mt-8 flex justify-center">
              <Button onClick={() => selectedService && setStep(2)} disabled={!selectedService} size="lg">
                Dalej
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Select Date & Time */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <h2 className="text-xl font-semibold mb-6 text-center">Wybierz termin</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Calendar */}
              <Card className="bg-card border-border">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg">
                    {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </CardTitle>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {DAYS_SHORT.map(day => (
                      <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {getDaysInMonth(currentMonth).map((day, idx) => {
                      const available = isDateAvailable(day);
                      const isSelected = day && selectedDate && day.toDateString() === selectedDate.toDateString();
                      
                      return (
                        <button
                          key={idx}
                          onClick={() => available && setSelectedDate(day)}
                          disabled={!available}
                          className={`aspect-square flex items-center justify-center text-sm rounded-sm transition-colors ${
                            !day ? 'invisible' :
                            !available ? 'text-muted-foreground/30 cursor-not-allowed' :
                            isSelected ? 'bg-primary text-primary-foreground' :
                            'hover:bg-secondary'
                          }`}
                        >
                          {day?.getDate()}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Time Slots */}
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="text-lg">
                    {selectedDate ? selectedDate.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Wybierz dzień'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedDate ? (
                    <div className="grid grid-cols-3 gap-2">
                      {getAvailableTimes().length > 0 ? (
                        getAvailableTimes().map(time => (
                          <button
                            key={time}
                            onClick={() => setSelectedTime(time)}
                            className={`py-3 px-4 text-sm rounded-sm border transition-colors ${
                              selectedTime === time 
                                ? 'bg-primary text-primary-foreground border-primary' 
                                : 'border-border hover:border-primary'
                            }`}
                          >
                            {time}
                          </button>
                        ))
                      ) : (
                        <p className="col-span-3 text-center text-muted-foreground py-8">
                          Brak dostępnych terminów w tym dniu
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      Wybierz dzień z kalendarza
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
            <div className="mt-8 flex justify-center gap-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Wstecz
              </Button>
              <Button onClick={() => selectedDate && selectedTime && setStep(3)} disabled={!selectedDate || !selectedTime}>
                Dalej
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Contact Details */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <h2 className="text-xl font-semibold mb-6 text-center">Twoje dane</h2>
            <Card className="max-w-md mx-auto">
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Imię i nazwisko *
                  </Label>
                  <Input 
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder="Jan Kowalski"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email *
                  </Label>
                  <Input 
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    placeholder="jan@example.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Telefon
                  </Label>
                  <Input 
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    placeholder="+48 123 456 789"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Dodatkowe informacje
                  </Label>
                  <Textarea 
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Np. lokalizacja sesji, szczególne życzenia..."
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {/* Summary */}
                <div className="pt-4 border-t border-border">
                  <h4 className="font-medium mb-2">Podsumowanie</h4>
                  <div className="text-sm space-y-1">
                    <p><span className="text-muted-foreground">Usługa:</span> {selectedService?.name}</p>
                    <p><span className="text-muted-foreground">Data:</span> {selectedDate?.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    <p><span className="text-muted-foreground">Godzina:</span> {selectedTime}</p>
                    <p className="text-lg font-bold mt-2">Cena: {selectedService?.price} zł</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="mt-8 flex justify-center gap-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Wstecz
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={submitting || !formData.client_name || !formData.client_email}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Zarezerwuj
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && bookingComplete && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Rezerwacja wysłana!</h2>
            <p className="text-muted-foreground mb-6">
              Twoja rezerwacja została wysłana do fotografa. Otrzymasz potwierdzenie na email.
            </p>
            <Card className="max-w-md mx-auto">
              <CardContent className="pt-6 text-left">
                <h4 className="font-medium mb-3">Szczegóły rezerwacji</h4>
                <div className="space-y-2 text-sm">
                  <p><span className="text-muted-foreground">Usługa:</span> {selectedService?.name}</p>
                  <p><span className="text-muted-foreground">Data:</span> {selectedDate?.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  <p><span className="text-muted-foreground">Godzina:</span> {selectedTime}</p>
                  <p><span className="text-muted-foreground">Fotograf:</span> {photographer.name}</p>
                  <p className="font-bold mt-2">Cena: {selectedService?.price} zł</p>
                </div>
              </CardContent>
            </Card>
            <div className="mt-8">
              <Link to={`/p/${subdomain}`}>
                <Button variant="outline" size="lg">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Wróć do strony fotografa
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
